import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Zap, ArrowLeft, Target, Pencil, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { value: "own", label: "FUTURO PDV (nativo)", help: "Rastreia conversões dentro do próprio FUTURO ADS." },
  { value: "meta", label: "Meta (Facebook/Instagram)", help: "ID do Pixel Meta + Access Token da Conversions API." },
  { value: "tiktok", label: "TikTok", help: "Pixel ID + Access Token da Events API." },
  { value: "google", label: "Google Ads", help: "Conversion ID + Conversion Label ou API Secret." },
];
const PLATFORM_LABEL = Object.fromEntries(PLATFORMS.map((p) => [p.value, p.label]));
const platformLabel = (v) => PLATFORM_LABEL[v] || PLATFORM_LABEL.own;

export default function AdsPixels() {
  const emptyForm = { name: "", platform: "own", external_id: "", external_access_token: "" };
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [snippetData, setSnippetData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ads2/pixels`);
      setPixels(r.data || []);
    } catch { /* empty */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/ads2/pixels/${editingId}`, form);
        toast.success("Pixel atualizado");
      } else {
        await axios.post(`${API}/ads2/pixels`, form);
        toast.success("Pixel criado");
      }
      setForm(emptyForm); setEditingId(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditingId(p.pixel_id);
    setForm({
      name: p.name || "", platform: p.platform || "own",
      external_id: p.external_id || "", external_access_token: p.external_access_token || "",
    });
  };

  const remove = async (pixel_id) => {
    if (!window.confirm("Excluir este pixel? Todos os eventos coletados serão apagados.")) return;
    try {
      await axios.delete(`${API}/ads2/pixels/${pixel_id}`);
      toast.success("Pixel excluído"); load();
    } catch { toast.error("Erro ao excluir"); }
  };

  const showSnippet = async (pixel_id) => {
    try {
      const r = await axios.get(`${API}/ads2/pixels/${pixel_id}/snippet`);
      setSnippetData(r.data);
      setSnippetOpen(true);
    } catch { toast.error("Erro ao gerar snippet"); }
  };

  const testPixel = async (pixel_id) => {
    try {
      await axios.post(`${API}/ads2/pixels/${pixel_id}/test`);
      toast.success("Evento de teste enviado!");
      load();
    } catch { toast.error("Erro ao testar pixel"); }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippetData.snippet);
      toast.success("Copiado!");
    } catch { toast.error("Copie manualmente"); }
  };

  const platformMeta = PLATFORMS.find((p) => p.value === form.platform) || PLATFORMS[0];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6" data-testid="ads-pixels-page">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/ads-manager"><ArrowLeft className="w-4 h-4 mr-1" /> Ads Manager</Link>
        </Button>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Pixels de conversão</h1>
        <p className="text-muted-foreground mt-1">Rastreie conversões dentro do FUTURO PDV ou integre com Meta, TikTok e Google.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>{editingId ? "Editar pixel" : "Criar novo pixel"}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Site principal" data-testid="pixel-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Plataforma</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger data-testid="pixel-platform-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.platform !== "own" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>ID do pixel na plataforma</Label>
                <Input value={form.external_id}
                  onChange={(e) => setForm({ ...form, external_id: e.target.value })}
                  placeholder={form.platform === "meta" ? "Ex.: 1234567890123456"
                    : form.platform === "tiktok" ? "Ex.: C1AB2CD3EF4GH5IJK"
                    : "Ex.: AW-123456789/AbCdEfG"}
                  data-testid="pixel-external-id" />
              </div>
              <div className="space-y-1">
                <Label>Access Token / Chave da API</Label>
                <Input type="password" value={form.external_access_token}
                  onChange={(e) => setForm({ ...form, external_access_token: e.target.value })}
                  placeholder="Cole o token de acesso"
                  data-testid="pixel-external-token" />
              </div>
              <p className="text-xs text-muted-foreground md:col-span-2">{platformMeta.help}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={!form.name.trim() || saving} data-testid="create-pixel-btn">
              {saving ? "Salvando..." : editingId ? "Atualizar" : (<><Plus className="w-4 h-4 mr-1" /> Criar</>)}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancelar</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Seus pixels ({pixels.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
            : pixels.length === 0 ? (
            <div className="text-center py-8" data-testid="empty-pixels">
              <Target className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhum pixel criado. Crie o primeiro acima.</p>
            </div>
          ) : (
            <div className="divide-y">
              {pixels.map((p) => {
                const active = p.events_last_24h > 0 || !!p.last_event_at;
                const platform = p.platform || "own";
                return (
                  <div key={p.pixel_id} className="py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`pixel-row-${p.pixel_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{p.name}</span>
                        <Badge variant="outline">{platformLabel(platform)}</Badge>
                        <Badge className={active ? "bg-emerald-500 text-white" : "bg-slate-300 text-slate-700"}>
                          {active ? "🟢 Ativo" : "⚪ Aguardando"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 font-mono">{p.pixel_id}</p>
                      {p.external_id && <p className="text-xs text-muted-foreground">Externo: {p.external_id}</p>}
                      <p className="text-xs text-muted-foreground">
                        Eventos 24h: {p.events_last_24h || 0}
                        {p.last_event_at && <> · último há {timeAgo(p.last_event_at)}</>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {platform === "own" && (
                        <Button size="sm" variant="outline" onClick={() => showSnippet(p.pixel_id)} data-testid={`snippet-${p.pixel_id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Código
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => testPixel(p.pixel_id)} data-testid={`test-${p.pixel_id}`}>
                        <Zap className="w-3.5 h-3.5 mr-1" /> Testar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`edit-pixel-${p.pixel_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => remove(p.pixel_id)} data-testid={`delete-pixel-${p.pixel_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertDescription>
          <b>Pixels ilimitados</b> · Você pode criar quantos pixels quiser. Ao criar uma campanha, o pixel é opcional — se escolher um, as conversões serão vinculadas a ele.
        </AlertDescription>
      </Alert>

      <Dialog open={snippetOpen} onOpenChange={setSnippetOpen}>
        <DialogContent className="max-w-2xl" data-testid="snippet-dialog">
          <DialogHeader><DialogTitle>Código do pixel</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Cole este código dentro da tag &lt;head&gt; do seu site.</p>
          <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-lg overflow-auto max-h-80 whitespace-pre-wrap break-all">{snippetData?.snippet}</pre>
          <DialogFooter>
            <Button onClick={copySnippet} data-testid="copy-snippet-btn"><Copy className="w-4 h-4 mr-1" /> Copiar código</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
